<script lang="ts" setup>
import { labelColors } from "@/composables/colors";
import { watch, ref } from "vue";

const props = defineProps({
    dataSet: {
    type: Object as () =>  Record<string, number>,
        required: true,
    },
    partyType: {
        type: String,
        required: true,
    },
});

const graphData = ref([]);
const addressCountTotal = ref(0);

function sortLabelValue (label: string) {
    const match = label.match(/^([A-Z]+)(\+*)$/i);

    if (!match) return 999;

    const letter = match[1].toUpperCase();
    const plusCount = match[2]?.length || 0;
    const base = letter.charCodeAt(0) - 65;

    return base * 10 - plusCount;
}


const calculateChartValues = () => {
    const addressCounts = Object.values(props.dataSet);
    addressCountTotal.value = addressCounts.reduce((acc, val) => acc + val, 0);
    const percent = 100 / addressCountTotal.value;

    let labelData = Object.entries(props.dataSet)
        .filter(([label]) => label !== "?" && label !== "");

    if (props.dataSet.hasOwnProperty("?") || props.dataSet.hasOwnProperty("")) {
        labelData = [
            ...labelData,
            ["?", (props.dataSet["?"] ?? 0) + (props.dataSet[""] ?? 0)],
        ];
    }

    graphData.value = labelData
        .map(([label, buildingCount]) => ({
            label,
            color: labelColors[label],
            buildingCount,
            percentage: Math.round(buildingCount * percent),
        }))
        .sort((a, b) => sortLabelValue(a.label) - sortLabelValue(b.label));
};

watch(
    () => props.dataSet,
    () => calculateChartValues(),
    { immediate: true }
);
</script>   


<template>
    <div class="energylabels-wrapper" :class="{ 'energylabels-wrapper--homes': props.partyType === 'Homes' }">

        <v-container class="label-overview-container-large pa-0">
            <v-row dense>
                <v-col cols="12" class="text-medium-emphasis font-weight-medium pb-1">
                    Distributie van {{ addressCountTotal }} {{ props.partyType === "Homes" || props.partyType === "EnergyLabels" ? "adressen" : "gebouwen" }}
                </v-col>
            </v-row>
            <v-row v-for="labelData in graphData" :key="labelData.label" class="labeldata-small" align="center" dense>
                <v-col cols="4" no-gutters>
                    <energy-label :label="labelData.label" :estimate="false" class="smaller" />
                </v-col>
                <v-col cols="4" no-gutters>
                    <span class="object-count">
                        {{ labelData.buildingCount }} adres{{ labelData.buildingCount !== 1 ? "sen" : "" }}
                    </span>
                </v-col>
                <v-col cols="4" no-gutters>
                    <v-container class="progressbar-large pa-0">
                        <v-row align="center" class="ga-0" dense>
                            <v-col cols="9">
                                <v-progress-linear :model-value="labelData.percentage"
                                    color="rgb(var(--v-theme-primary))" height="10px" rounded />
                            </v-col>
                            <v-col cols="3">
                                {{ labelData.percentage }}%
                            </v-col>
                        </v-row>
                    </v-container>
                </v-col>
            </v-row>
        </v-container>

        <v-container class="label-overview-container-small pa-0">
            <v-row>
                <v-col cols="12">
                    Distributie van {{ addressCountTotal }} {{ props.partyType === "Homes" || props.partyType === "EnergyLabels" ? "adressen" : "gebouwen" }}
                </v-col>
            </v-row>
            <v-row v-for="labelData in graphData" class="labeldata-small" align="center" dense>
                <v-col cols="3" no-gutters>
                    <energy-label :label="labelData.label" :estimate="false" class="smaller" />
                </v-col>
                <v-col cols="5" no-gutters>
                    <span class="object-count">
                        {{ labelData.buildingCount }} adres{{ labelData.buildingCount !== 1 ? "sen" : "" }}
                    </span>
                </v-col>
                <v-col cols="4" no-gutters>
                    <v-progress-linear :model-value="labelData.percentage" color="rgb(var(--v-theme-primary))"
                        height="20px" rounded>
                        {{ labelData.percentage }}%
                    </v-progress-linear>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<style scoped>

.energylabels-wrapper {
    container-type: inline-size;
    container-name: energylabels;
}

.energylabels-wrapper--homes .label-overview-container-large {
    font-size: 0.8rem;
}

.energylabels-wrapper--homes .label-overview-container-large .v-col {
    padding-top: 2px;
    padding-bottom: 2px;
}

.label-overview-container-large .labeldata-small .v-col {
    padding-top: 5px;
    padding-bottom: 2px;
}

.label-overview-container-large .labeldata-small + .labeldata-small {
    margin-top: 0;
}

.energylabels-wrapper--homes .label-overview-container-large .object-count {
    font-size: 0.75rem;
}

.energylabels-wrapper--homes .label-overview-container-large .labeldata-small {
    margin: 0;
    min-height: 28px;
}

.energylabels-wrapper--homes .label-overview-container-large .labeldata-small .v-col {
    line-height: 1.05;
}

.energylabels-wrapper--homes .label-overview-container-large .text-medium-emphasis {
    font-size: 0.78rem;
    line-height: 1.2;
    margin-bottom: 8px;
}

.energylabels-wrapper--homes .label-overview-container-large .v-progress-linear {
    height: 8px;
}

.energylabels-wrapper--homes :deep(.energy-label-wrap) {
    font-size: 0.8rem;
    height: 26px;
    width: 45px;
    padding-left: 0;
    justify-content: flex-start;
    padding: 0 3px;
}

.energylabels-wrapper--homes :deep(.icon-energy-label::before) {
    font-size: 26px !important;
}

@container energylabels (width < 280px) {

    /* kleine variant */

    .label-overview-container-large {
        display: none;
    }

    .label-overview-container-small {
        font-size: 0.75rem;
        display: block;
    }

    .object-count {
        padding-left: 5px;
    }

    :deep(.energy-label-wrap) {
        font-size: 0.8rem;
        height: 30px;

        .icon-energy-label::before {
            font-size: 30px !important;
        }
    }
}

@container energylabels (width >=290px) {

    /* grote variant */

    .label-overview-container-large {
        display: block;
        font-size: 1rem;
    }

    .label-overview-container-small {
        display: none;
    }

}
</style>
